/**
 * Export.tsx — Download center for enriched client data and the master registry,
 * plus API key management (admins only).
 *
 * Rebuilt in final pass.
 *
 * Preserves all behavior:
 *   - Client Export — downloads <client>_meridian_export.xlsx
 *   - Master Registry Export — admin only, two-sheet XLSX
 *   - API Access section — admin only: generate key, display once, revoke
 *   - viewAsClient auto-selection (admin previewing a client)
 *   - blob download with content-disposition filename parsing
 *   - posthog analytics event capture
 *
 * Changes:
 *   - Dropped the inline API Documentation card — links to /api-docs instead
 *   - Uses shared UI (Card, Button, Tag, Input, Select, toast, Modal)
 *   - API key reveal uses toast.success for copy confirmation
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import { captureEvent } from '../../analytics/posthog';
import {
  Card,
  Button, IconButton,
  Tag,
  Input, Select,
  Icon,
  Skeleton,
  EmptyState,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface Stats {
  registry_outlets: number;
  active_clients: number;
}

interface ApiKeyInfo {
  key_id: string;
  client_name: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  request_count: number;
}

/* ═══════════════ PAGE ═══════════════ */

export default function Export() {
  const { adminMode, maskClientName, viewAsClient, modeVersion } = useAdmin();

  const [clients, setClients] = useState<string[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrichedLoading, setEnrichedLoading] = useState(false);
  const [registryLoading, setRegistryLoading] = useState(false);

  // API key management (admin-only)
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [newKeyClient, setNewKeyClient] = useState('');
  const [newKeyResult, setNewKeyResult] = useState<string | null>(null);
  const [keyLoading, setKeyLoading] = useState(false);

  useEffect(() => {
    document.title = 'Export | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Load clients + stats
  useEffect(() => {
    async function load() {
      try {
        const [cRes, sRes] = await Promise.all([
          api.get<string[]>('/api/clients'),
          api.get<Stats>('/api/stats'),
        ]);
        setClients(cRes.data);
        setStats(sRes.data);
        if (cRes.data.length > 0) setSelected(cRes.data[0]);
      } catch {
        toast.error('Failed to load clients');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [modeVersion]);

  // Auto-select viewAsClient
  useEffect(() => {
    if (viewAsClient && clients.includes(viewAsClient)) {
      setSelected(viewAsClient);
    }
  }, [viewAsClient, clients]);

  // Load API keys when admin mode is on
  useEffect(() => {
    if (adminMode) {
      api.get<ApiKeyInfo[]>('/api/v1/keys')
        .then(r => setApiKeys(r.data))
        .catch(() => {});
    }
  }, [adminMode]);

  /** Generic blob download. Parses content-disposition for the filename. */
  const download = async (url: string, fallbackName: string, setDlLoading: (v: boolean) => void) => {
    setDlLoading(true);
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const cd = res.headers['content-disposition'];
      let filename = fallbackName;
      if (cd) {
        const match = cd.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(href);
      captureEvent('export_downloaded', { url, filename });
      toast.success('Download started', filename);
    } catch {
      toast.error('Download failed');
    } finally {
      setDlLoading(false);
    }
  };

  const generateKey = async () => {
    if (!newKeyClient.trim()) return;
    setKeyLoading(true);
    setNewKeyResult(null);
    try {
      const res = await api.post('/api/v1/keys', { client_name: newKeyClient.trim() });
      setNewKeyResult(res.data.raw_key);
      setNewKeyClient('');
      const keysRes = await api.get<ApiKeyInfo[]>('/api/v1/keys');
      setApiKeys(keysRes.data);
      toast.success('API key generated', 'Copy it now — it will not be shown again.');
    } catch {
      toast.error('Failed to generate key');
    } finally {
      setKeyLoading(false);
    }
  };

  const revokeKey = async (keyId: string) => {
    try {
      await api.delete(`/api/v1/keys/${keyId}`);
      const keysRes = await api.get<ApiKeyInfo[]>('/api/v1/keys');
      setApiKeys(keysRes.data);
      toast.success('API key revoked');
    } catch {
      toast.error('Failed to revoke key');
    }
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success('Key copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <Skeleton width={180} height={28} />
        <div style={{ marginTop: 16 }}>
          <Skeleton width={320} height={14} />
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20, marginTop: 32,
        }}>
          {[0, 1].map(i => (
            <Card key={i}>
              <Skeleton width={180} height={16} />
              <div style={{ marginTop: 12 }}><Skeleton width="100%" height={12} /></div>
              <div style={{ marginTop: 8 }}><Skeleton width="80%" height={12} /></div>
              <div style={{ marginTop: 20 }}><Skeleton width={140} height={36} /></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 32 }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary,
          margin: 0,
          letterSpacing: '-0.02em',
        }}>Export</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Download enriched client files and the master registry</div>

        {stats && (
          <div style={{
            color: theme.text.secondary,
            fontSize: 14,
            marginTop: 14,
          }}>
            Registry contains{' '}
            <strong style={{
              color: theme.brand.cyanLight,
              fontFamily: theme.font.data,
              fontWeight: 700,
            }}>{stats.registry_outlets.toLocaleString()}</strong>
            {' '}outlets from{' '}
            <strong style={{
              color: theme.brand.cyanLight,
              fontFamily: theme.font.data,
              fontWeight: 700,
            }}>{stats.active_clients}</strong>
            {' '}clients.
          </div>
        )}
      </div>

      {/* Two-column export cards */}
      <div
        className="meridian-export-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 20,
          marginBottom: 32,
        }}>
        <style>{`
          @media (min-width: 900px) {
            .meridian-export-grid {
              grid-template-columns: ${adminMode ? '1fr 1fr' : '1fr'} !important;
              max-width: ${adminMode ? 'none' : '640px'} !important;
            }
          }
        `}</style>

        {/* Client enriched export */}
        <Card title="Client export">
          <div style={{
            color: theme.text.secondary,
            fontSize: 13,
            lineHeight: 1.65,
            marginBottom: 18,
          }}>
            Single file per client with Meridian + Client dual-layer columns:
            Census ID, tax ID, Name / Address / City / GPS (Meridian &amp; Client),
            Branch, Outlet Format, Region, company status, Match Status
            (Matched / Unmatched / Distribution Gap), Confidence Score, and Match Method.
          </div>

          {/* Client selector or locked display */}
          <div style={{ marginBottom: 18 }}>
            {viewAsClient ? (
              <div>
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  color: theme.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 6,
                }}>Client</div>
                <div style={{
                  padding: '10px 14px',
                  background: theme.surface.inset,
                  border: `1px solid ${theme.borderColor.subtle}`,
                  borderRadius: 8,
                  color: theme.brand.cyanLight,
                  fontWeight: 600,
                  fontSize: 14,
                }}>{maskClientName(viewAsClient)}</div>
              </div>
            ) : (
              <Select
                value={selected}
                onChange={e => setSelected(e.target.value)}
              >
                {clients.map((c, i) => (
                  <option key={c} value={c}>{maskClientName(c, i)}</option>
                ))}
              </Select>
            )}
          </div>

          <Button
            variant="primary"
            loading={enrichedLoading}
            disabled={!selected || enrichedLoading}
            onClick={() =>
              download(
                `/api/export/enriched?client=${encodeURIComponent(selected)}`,
                `${selected}_meridian_export.xlsx`,
                setEnrichedLoading,
              )
            }
            leftIcon={<Icon.Download size={14} />}
          >
            {enrichedLoading ? 'Generating…' : 'Generate client export'}
          </Button>
        </Card>

        {/* Master Registry — admin only */}
        {adminMode && (
          <Card title="Master registry export">
            <div style={{
              color: theme.text.secondary,
              fontSize: 13,
              lineHeight: 1.65,
              marginBottom: 18,
            }}>
              Download the complete outlet registry as a two-sheet XLSX.
              Sheet 1: Master Registry (all outlets with canonical fields, branch,
              region, company status). Sheet 2: Cross Reference (Census ID to
              client record mappings).
            </div>

            <Button
              variant="primary"
              loading={registryLoading}
              disabled={registryLoading}
              onClick={() =>
                download('/api/export/registry', 'meridian_registry.xlsx', setRegistryLoading)
              }
              leftIcon={<Icon.Download size={14} />}
            >
              {registryLoading ? 'Generating…' : 'Generate master registry'}
            </Button>
          </Card>
        )}
      </div>

      {/* API Access — admin only */}
      {adminMode && (
        <div>
          <div style={{
            fontSize: 11, fontWeight: 600,
            color: theme.text.tertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            marginBottom: 14,
          }}>API Access</div>

          <div
            className="meridian-api-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 20,
            }}>
            <style>{`
              @media (min-width: 900px) {
                .meridian-api-grid { grid-template-columns: 1fr 1fr !important; }
              }
            `}</style>

            {/* Documentation link card */}
            <Card
              title="REST API"
              subtitle="Integrate Census IDs directly into your ERP and reporting systems."
            >
              <div style={{
                background: theme.surface.inset,
                border: `1px solid ${theme.borderColor.default}`,
                borderRadius: 8,
                padding: '12px 14px',
                fontFamily: theme.font.data,
                fontSize: 12,
                color: theme.text.secondary,
                lineHeight: 1.9,
                overflowX: 'auto',
                whiteSpace: 'nowrap',
              }}>
                <div><ApiMethod>GET</ApiMethod> /api/v1/outlet/<ApiParam>census_id</ApiParam></div>
                <div><ApiMethod>GET</ApiMethod> /api/v1/outlet/<ApiParam>census_id</ApiParam>/clients</div>
                <div><ApiMethod>GET</ApiMethod> /api/v1/search?name=&city=</div>
                <div><ApiMethod>GET</ApiMethod> /api/v1/me</div>
              </div>
              <div style={{ marginTop: 14 }}>
                <Link to="/api-docs" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    rightIcon={<Icon.ExternalLink size={12} />}
                  >View full documentation</Button>
                </Link>
              </div>
            </Card>

            {/* Key management card */}
            <Card
              title="Manage API keys"
              subtitle="Keys are shown once at creation — copy immediately."
            >
              {/* Generate */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={newKeyClient}
                    onChange={e => setNewKeyClient(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && generateKey()}
                    placeholder="Client name…"
                  />
                </div>
                <Button
                  variant="primary"
                  size="md"
                  loading={keyLoading}
                  disabled={keyLoading || !newKeyClient.trim()}
                  onClick={generateKey}
                >Generate</Button>
              </div>

              {/* New key reveal */}
              {newKeyResult && (
                <div style={{
                  background: theme.semantic.successSoft,
                  border: `1px solid ${theme.semantic.successBorder}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 16,
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 8,
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600,
                      color: theme.semantic.warning,
                      letterSpacing: '0.05em',
                    }}>NOT SHOWN AGAIN — COPY NOW</span>
                    <IconButton
                      icon={<Icon.Copy size={12} />}
                      size="sm"
                      onClick={() => copyKey(newKeyResult)}
                      aria-label="Copy key"
                    />
                  </div>
                  <div style={{
                    fontFamily: theme.font.data,
                    fontSize: 12,
                    color: theme.semantic.success,
                    wordBreak: 'break-all',
                  }}>{newKeyResult}</div>
                </div>
              )}

              {/* Existing keys */}
              {apiKeys.length === 0 ? (
                <EmptyState
                  icon={<Icon.Shield size={22} />}
                  title="No API keys yet"
                  description="Generate one above to enable API access for a client."
                  compact
                />
              ) : (
                <div style={{
                  border: `1px solid ${theme.borderColor.subtle}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}>
                  {apiKeys.map((k, i) => (
                    <div key={k.key_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : `1px solid ${theme.borderColor.subtle}`,
                      gap: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: theme.text.primary,
                          fontWeight: 500,
                          fontSize: 13,
                        }}>{k.client_name}</div>
                        <div style={{
                          color: theme.text.tertiary,
                          fontSize: 11,
                          marginTop: 2,
                          fontFamily: theme.font.data,
                        }}>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{k.request_count.toLocaleString()}</span> requests
                        </div>
                      </div>
                      {k.is_active ? (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => revokeKey(k.key_id)}
                        >Revoke</Button>
                      ) : (
                        <Tag kind="danger">Revoked</Tag>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ INLINE METHOD TOKENS ═══════════════ */

function ApiMethod({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      color: theme.brand.cyan,
      fontWeight: 700,
      marginRight: 6,
    }}>{children}</span>
  );
}

function ApiParam({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ color: theme.semantic.success }}>{'{'}{children}{'}'}</span>
  );
}
